import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RosterAction, RosterRow } from '@mdt/contracts';
import { Alert, Button, Dialog, ErrorAlert, Loading, SelectField, TextArea, TextField } from '../components/kit.tsx';
import { SafeLink } from '../components/SafeLink.tsx';
import { command, query } from '../lib/api.ts';
import { bangkokParts, useFormat, usePageTitle, usePersonName } from '../lib/format.ts';
import { useRouter } from '../lib/router.tsx';
import type { SheetResult } from '../lib/roster/columns.ts';
import type { CohortRow } from './AdminCohorts.tsx';

type Member = { membershipId: string; personId: string; studentId: string | null; nameTh: string; nameEn: string | null; role: string; state: string };
type ProjectRow = { projectId: string; projectCode: string; state: string; maxMembers: number; titleTh: string | null; titleEn: string | null; members: Member[] };
type PoolRow = { personId: string; studentId: string | null; nameTh: string; nameEn: string | null; status: string | null; openInvitations: number };
type RosterEntry = {
  personId: string; studentId: string | null; email: string | null; nameTh: string; nameEn: string | null; accountLinked: boolean;
  source: string; enrolledAt: string; status: string | null; projectCode: string | null;
};

/** One cohort: roster import (4.1), the orphan pool (4.5), groups and size limits (4.3), the roster and academic status (4.4). */
export function AdminCohort() {
  const { t, i18n } = useTranslation();
  const { search } = useRouter();
  const cohortId = search.get('id') ?? '';
  const cohorts = useQuery({ queryKey: ['cohort.list'], queryFn: () => query<CohortRow[]>('cohort.list') });
  const cohort = cohorts.data?.find((c) => c.cohortId === cohortId);
  usePageTitle(cohort ? t('adminCohort.title', { code: cohort.code }) : t('adminCohorts.title'));
  if (cohorts.isPending) return <Loading />;
  if (!cohort) {
    return (
      <div className="stack">
        <h1>{t('notFound.title')}</h1>
        <ErrorAlert error={cohorts.error} />
        <p><SafeLink href="/admin/cohorts">{t('adminCohorts.title')}</SafeLink></p>
      </div>
    );
  }
  return (
    <div className="stack">
      <p><SafeLink href="/admin/cohorts">{t('adminCohort.back')}</SafeLink></p>
      <h1>{t('adminCohort.title', { code: cohort.code })}</h1>
      <p className="muted">
        {i18n.language !== 'th' && cohort.labelEn ? cohort.labelEn : cohort.labelTh} · {t(`adminCohorts.states.${cohort.state}`)} ·{' '}
        {t('adminCohort.counts', { enrolled: cohort.enrolled, projects: cohort.projects, unplaced: cohort.unplaced })}
      </p>
      <p><SafeLink href={`/admin/rounds?cohort=${cohortId}`}>{t('adminCohort.rounds')}</SafeLink></p>
      <p><SafeLink href={`/admin/matching?cohort=${cohortId}`}>{t('adminCohort.matching')}</SafeLink></p>
      {cohort.state !== 'archived' ? <RosterImport cohortId={cohortId} /> : null}
      <Pool cohortId={cohortId} />
      <Groups cohortId={cohortId} />
      <Roster cohortId={cohortId} />
    </div>
  );
}

function useInvalidateCohort(cohortId: string) {
  const qc = useQueryClient();
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: ['cohort.list'] }),
    qc.invalidateQueries({ queryKey: ['cohort.roster', cohortId] }),
    qc.invalidateQueries({ queryKey: ['cohort.projects', cohortId] }),
    qc.invalidateQueries({ queryKey: ['pool.list', cohortId] }),
  ]);
}

// ---------------------------------------------------------------------------------------------------
// Roster import: the file is read here (never uploaded as a file), previewed by the server, then committed
// with the preview token. If anything changed in between the server refuses and asks for a new preview.
// ---------------------------------------------------------------------------------------------------
type Preview = { previewToken?: string; committed: boolean; actions: RosterAction[] };

function RosterImport({ cohortId }: { cohortId: string }) {
  const { t } = useTranslation();
  const inputId = useId();
  const invalidate = useInvalidateCohort(cohortId);
  const [sheet, setSheet] = useState<(SheetResult & { fileName: string }) | null>(null);
  const [readError, setReadError] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState<Preview | null>(null);
  const run = useMutation({
    mutationFn: (v: { mode: 'preview' | 'commit'; rows: RosterRow[]; previewToken?: string }) =>
      command<Preview>('cohort.importRoster', { cohortId, ...v }),
    onSuccess: async (r, v) => {
      if (v.mode === 'preview') {
        setPreview(r.result);
        return;
      }
      setDone(r.result);
      setPreview(null);
      setSheet(null);
      await invalidate();
    },
  });

  const choose = async (file: File | undefined) => {
    setPreview(null);
    setDone(null);
    setReadError(false);
    run.reset();
    if (!file) {
      setSheet(null);
      return;
    }
    try {
      const { readRosterFile } = await import('../lib/roster/columns.ts');
      setSheet({ ...(await readRosterFile(file)), fileName: file.name });
    } catch {
      setSheet(null);
      setReadError(true);
    }
  };

  const counts = preview ? countActions(preview.actions) : null;
  const shown = preview?.actions.filter((a) => a.action !== 'no_op') ?? [];
  return (
    <section className="card" aria-labelledby="roster-import">
      <h2 id="roster-import">{t('roster.title')}</h2>
      <p>{t('roster.intro')}</p>
      <div className="field">
        <label className="field__label" htmlFor={inputId}>{t('roster.file')}</label>
        <span className="field__hint" id={`${inputId}-hint`}>{t('roster.fileHint')}</span>
        <input id={inputId} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          aria-describedby={`${inputId}-hint`} onChange={(e) => void choose(e.currentTarget.files?.[0])} />
      </div>
      {readError ? <Alert tone="error"><p>{t('roster.unreadable')}</p></Alert> : null}
      {done ? <Alert tone="success"><p data-testid="roster-done">{t('roster.committed', { count: done.actions.filter((a) => a.action !== 'no_op').length })}</p></Alert> : null}
      {sheet ? (
        <>
          <p>{t('roster.read', { count: sheet.rows.length, file: sheet.fileName })}</p>
          {sheet.problems.length ? (
            <Alert tone="error" title={t('roster.fileProblems')}>
              <ul>{sheet.problems.map((p) => <li key={`${p.line}-${p.key}`}>{t(`roster.problem.${p.key}`, { line: p.line, value: p.value ?? '' })}</li>)}</ul>
            </Alert>
          ) : (
            <Button busy={run.isPending && run.variables?.mode === 'preview'} onClick={() => run.mutate({ mode: 'preview', rows: sheet.rows })}>
              {t('roster.preview')}
            </Button>
          )}
        </>
      ) : null}
      <ErrorAlert error={run.error} />
      {preview && counts && sheet ? (
        <div className="stack" data-testid="roster-preview">
          <h3>{t('roster.previewTitle')}</h3>
          <ul className="row" aria-label={t('roster.summary')}>
            {(Object.keys(counts) as RosterAction['action'][]).map((k) => (
              <li key={k} className="tag" data-testid={`count-${k}`}>{t(`roster.actions.${k}`)}: {counts[k]}</li>
            ))}
          </ul>
          {shown.length ? (
            <div className="table-wrap">
              <table className="table">
                <caption className="visually-hidden">{t('roster.previewTitle')}</caption>
                <thead>
                  <tr>
                    <th scope="col" className="num">{t('roster.line')}</th>
                    <th scope="col">{t('roster.studentId')}</th>
                    <th scope="col">{t('roster.name')}</th>
                    <th scope="col">{t('roster.action')}</th>
                    <th scope="col">{t('roster.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((a) => {
                    const row = sheet.rows[a.row - 1];
                    return (
                      <tr key={a.row} className={a.action === 'error' ? 'row--error' : undefined}>
                        <td className="num">{sheet.lines[a.row - 1]}</td>
                        <td>{row?.studentId}</td>
                        <td>{row?.nameTh}</td>
                        <td>{t(`roster.actions.${a.action}`)}</td>
                        <td>{describe(t, a)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p>{t('roster.nothingToDo')}</p>}
          {counts.error > 0 ? <Alert tone="warning"><p>{t('roster.fixErrors')}</p></Alert> : (
            <Button busy={run.isPending && run.variables?.mode === 'commit'} disabled={shown.length === 0}
              onClick={() => run.mutate({ mode: 'commit', rows: sheet.rows, previewToken: preview.previewToken! })}>
              {t('roster.commit')}
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
}

const countActions = (actions: RosterAction[]) => {
  const c = { create: 0, enroll_prefix: 0, re_enroll: 0, update: 0, no_op: 0, error: 0 } satisfies Record<RosterAction['action'], number>;
  for (const a of actions) c[a.action]++;
  return c;
};

function describe(t: TFunction, a: RosterAction): string {
  const parts: string[] = [];
  if (a.detail && a.detail !== 'changes') parts.push(t(`roster.detail.${a.detail}`));
  if (a.fromCohort) parts.push(t('roster.fromCohort', { code: a.fromCohort }));
  if (a.changes?.length) parts.push(t('roster.changes', { fields: a.changes.map((c) => t(`roster.fields.${c}`)).join(', ') }));
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------------------------------
// Orphan pool (4.5)
// ---------------------------------------------------------------------------------------------------
function Pool({ cohortId }: { cohortId: string }) {
  const { t } = useTranslation();
  const name = usePersonName();
  const invalidate = useInvalidateCohort(cohortId);
  const pool = useQuery({ queryKey: ['pool.list', cohortId], queryFn: () => query<PoolRow[]>('pool.list', { cohortId }) });
  const projects = useQuery({ queryKey: ['cohort.projects', cohortId], queryFn: () => query<ProjectRow[]>('cohort.projects', { cohortId }) });
  const [target, setTarget] = useState<{ person: PoolRow; mode: 'place' | 'solo' } | null>(null);
  const [projectId, setProjectId] = useState('');
  const [reason, setReason] = useState('');
  const [over, setOver] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: () => (target!.mode === 'place'
      ? command('pool.place', { personId: target!.person.personId, projectId, reason: reason.trim(),
        ...(over.trim() ? { overCapacityReason: over.trim() } : {}) })
      : command('pool.createSoloProject', { personId: target!.person.personId, reason: reason.trim() })),
    onSuccess: async () => {
      setNotice(target!.mode === 'place' ? t('pool.placed', { name: name(target!.person) }) : t('pool.soloCreated', { name: name(target!.person) }));
      setTarget(null);
      await invalidate();
    },
  });
  const open = (person: PoolRow, mode: 'place' | 'solo') => {
    setTarget({ person, mode });
    setProjectId('');
    setReason('');
    setOver('');
    setError(null);
    act.reset();
  };
  // before matching a forming group; after it a matched one too (7.5: its advisors' shares grow)
  const withRoom = (projects.data ?? []).filter((p) => (p.state === 'forming' || p.state === 'matched')
    && p.members.filter((m) => m.state !== 'invited').length < p.maxMembers);
  const chosen = withRoom.find((p) => p.projectId === projectId);
  const confirm = () => {
    if (target?.mode === 'place' && !projectId) {
      setError(t('pool.chooseProject'));
      return;
    }
    if (!reason.trim()) {
      setError(t('group.reasonRequired'));
      return;
    }
    setError(null);
    act.mutate();
  };
  return (
    <section className="card" aria-labelledby="pool">
      <h2 id="pool">{t('pool.title')}</h2>
      <p>{t('pool.intro')}</p>
      {notice ? <Alert tone="success"><p>{notice}</p></Alert> : null}
      <ErrorAlert error={pool.error} />
      {pool.isPending ? <Loading /> : null}
      {pool.data?.length === 0 ? <p>{t('pool.empty')}</p> : null}
      {pool.data?.length ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">{t('pool.title')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('roster.studentId')}</th>
                <th scope="col">{t('roster.name')}</th>
                <th scope="col">{t('roster.status')}</th>
                <th scope="col" className="num">{t('pool.openInvitations')}</th>
                <th scope="col">{t('adminCohorts.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pool.data.map((p) => (
                <tr key={p.personId} data-testid="pool-row">
                  <td>{p.studentId}</td>
                  <td>{name(p)}</td>
                  <td>{p.status ? t(`status.${p.status}`) : t('status.active')}</td>
                  <td className="num">{p.openInvitations}</td>
                  <td className="row">
                    <Button variant="secondary" onClick={() => open(p, 'place')} aria-label={t('pool.placeLabel', { name: name(p) })}>{t('pool.place')}</Button>
                    <Button variant="secondary" onClick={() => open(p, 'solo')} aria-label={t('pool.soloLabel', { name: name(p) })}>{t('pool.solo')}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <Dialog open={target !== null} title={target ? (target.mode === 'place' ? t('pool.placeTitle', { name: name(target.person) }) : t('pool.soloTitle', { name: name(target.person) })) : ''}
        onCancel={() => setTarget(null)} actions={(
          <>
            <Button busy={act.isPending} onClick={confirm}>{t('adminCohorts.confirm')}</Button>
            <Button variant="secondary" onClick={() => setTarget(null)}>{t('common.cancel')}</Button>
          </>
        )}>
        {target?.mode === 'place' ? (
          withRoom.length ? (
            <SelectField label={t('pool.project')} value={projectId} required onChange={setProjectId}
              options={withRoom.map((p) => ({ value: p.projectId,
                label: `${p.projectCode} (${p.members.filter((m) => m.state !== 'invited').length}/${p.maxMembers})${p.state === 'matched' ? ` · ${t('pool.matched')}` : ''}` }))} />
          ) : <p>{t('pool.noRoom')}</p>
        ) : <p>{t('pool.soloBody')}</p>}
        <TextArea label={t('group.reason')} value={reason} required maxLength={2000} onChange={(e) => setReason(e.currentTarget.value)} />
        {chosen?.state === 'matched' ? (
          <TextArea label={t('adminProject.over')} hint={t('adminProject.overHint')} value={over} maxLength={2000} onChange={(e) => setOver(e.currentTarget.value)} />
        ) : null}
        {error ? <Alert tone="error"><p>{error}</p></Alert> : null}
        <ErrorAlert error={act.error} />
      </Dialog>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------
// Groups and size limits (4.3)
// ---------------------------------------------------------------------------------------------------
function Groups({ cohortId }: { cohortId: string }) {
  const { t } = useTranslation();
  const name = usePersonName();
  const invalidate = useInvalidateCohort(cohortId);
  const projects = useQuery({ queryKey: ['cohort.projects', cohortId], queryFn: () => query<ProjectRow[]>('cohort.projects', { cohortId }) });
  const [target, setTarget] = useState<ProjectRow | null>(null);
  const [max, setMax] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cap = useMutation({
    mutationFn: () => command('project.setSizeCap', { projectId: target!.projectId, maxMembers: Number(max), reason: reason.trim() }),
    onSuccess: async () => {
      setTarget(null);
      await invalidate();
    },
  });
  const confirm = () => {
    const n = Number(max);
    if (!Number.isInteger(n) || n < 1 || n > 10) return setError(t('groups.maxInvalid'));
    if (reason.trim().length < 10) return setError(t('groups.reasonShort'));
    setError(null);
    cap.mutate();
  };
  const visible = (projects.data ?? []).filter((p) => p.state !== 'dissolved');
  return (
    <section className="card" aria-labelledby="groups">
      <h2 id="groups">{t('groups.title')}</h2>
      <ErrorAlert error={projects.error} />
      {projects.isPending ? <Loading /> : null}
      {projects.data && visible.length === 0 ? <p>{t('groups.empty')}</p> : null}
      {visible.length ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">{t('groups.title')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('groups.code')}</th>
                <th scope="col">{t('adminCohorts.state')}</th>
                <th scope="col">{t('groups.members')}</th>
                <th scope="col" className="num">{t('groups.size')}</th>
                <th scope="col">{t('adminCohorts.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const live = p.members.filter((m) => m.state !== 'invited');
                return (
                  <tr key={p.projectId} data-testid="group-row">
                    <th scope="row">{p.projectCode}</th>
                    <td>{t(`group.states.${p.state}`)}</td>
                    <td>
                      {live.map((m) => `${name(m)}${m.role === 'leader' ? ` (${t('group.leader')})` : ''}`).join(', ')}
                      {p.members.length > live.length ? <span className="muted small"> · {t('groups.pending', { count: p.members.length - live.length })}</span> : null}
                    </td>
                    <td className="num">{live.length}/{p.maxMembers}</td>
                    <td className="row">
                      <SafeLink href={`/admin/project?id=${p.projectId}`} aria-label={t('groups.manageLabel', { code: p.projectCode })}>{t('groups.manage')}</SafeLink>
                      {p.state === 'forming' || p.state === 'matched' ? (
                        <Button variant="secondary" aria-label={t('groups.capLabel', { code: p.projectCode })} onClick={() => {
                          setTarget(p);
                          setMax(String(p.maxMembers));
                          setReason('');
                          setError(null);
                          cap.reset();
                        }}>{t('groups.cap')}</Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      <Dialog open={target !== null} title={t('groups.capTitle', { code: target?.projectCode ?? '' })} onCancel={() => setTarget(null)} actions={(
        <>
          <Button busy={cap.isPending} onClick={confirm}>{t('adminCohorts.confirm')}</Button>
          <Button variant="secondary" onClick={() => setTarget(null)}>{t('common.cancel')}</Button>
        </>
      )}>
        <p>{t('groups.capBody')}</p>
        <TextField label={t('groups.max')} type="number" min={1} max={10} value={max} required onChange={(e) => setMax(e.currentTarget.value)} />
        <TextArea label={t('groups.reason')} hint={t('groups.reasonHint')} value={reason} required maxLength={2000} onChange={(e) => setReason(e.currentTarget.value)} />
        {error ? <Alert tone="error"><p>{error}</p></Alert> : null}
        <ErrorAlert error={cap.error} />
      </Dialog>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------
// Roster and academic status (4.4)
// ---------------------------------------------------------------------------------------------------
const STATUSES = ['active', 'leave_of_absence', 'withdrawn', 'graduated'] as const;

function Roster({ cohortId }: { cohortId: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const name = usePersonName();
  const invalidate = useInvalidateCohort(cohortId);
  const roster = useQuery({ queryKey: ['cohort.roster', cohortId], queryFn: () => query<RosterEntry[]>('cohort.roster', { cohortId }) });
  const [filter, setFilter] = useState('');
  const [target, setTarget] = useState<RosterEntry | null>(null);
  const [form, setForm] = useState<{ status: (typeof STATUSES)[number] | ''; date: string; docRef: string; reason: string }>({ status: '', date: '', docRef: '', reason: '' });
  const [error, setError] = useState<string | null>(null);
  const record = useMutation({
    mutationFn: () => {
      const r = target!;
      const docRef = form.docRef.trim() ? { docRef: form.docRef.trim() } : {};
      // withdrawal and leave (and coming back from leave) change the student's group and its load: roadmap 7.4's commands
      if (form.status === 'withdrawn' || form.status === 'leave_of_absence') {
        const today = bangkokParts(new Date()).date;
        return command('student.withdraw', { personId: r.personId, status: form.status, reason: form.reason.trim(), ...docRef,
          effectiveAt: form.date === today ? new Date().toISOString() : `${form.date}T00:00:00+07:00` });
      }
      if (form.status === 'active' && r.status === 'leave_of_absence') {
        return command('student.returnFromLeave', { personId: r.personId, reason: form.reason.trim() });
      }
      return command('student.recordStatus', {
        personId: r.personId, status: form.status, effectiveAt: `${form.date}T00:00:00+07:00`, reason: form.reason.trim(), ...docRef,
      });
    },
    onSuccess: async () => {
      setTarget(null);
      await invalidate();
    },
  });
  const confirm = () => {
    if (!form.status) return setError(t('status.chooseStatus'));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) return setError(t('status.dateRequired'));
    if (!form.reason.trim()) return setError(t('group.reasonRequired'));
    const applied = form.status === 'withdrawn' || form.status === 'leave_of_absence' || (form.status === 'active' && target?.status === 'leave_of_absence');
    if (applied && form.reason.trim().length < 10) return setError(t('adminProject.reasonShort', { n: 10 }));
    setError(null);
    record.mutate();
  };
  const needle = filter.trim().toLowerCase();
  const rows = (roster.data ?? []).filter((r) => !needle || [r.studentId, r.nameTh, r.nameEn, r.email, r.projectCode]
    .some((v) => v?.toLowerCase().includes(needle)));
  return (
    <section className="card" aria-labelledby="roster">
      <h2 id="roster">{t('rosterList.title')}</h2>
      <ErrorAlert error={roster.error} />
      {roster.isPending ? <Loading /> : null}
      {roster.data ? (
        <>
          <TextField label={t('rosterList.filter')} type="search" value={filter} onChange={(e) => setFilter(e.currentTarget.value)} />
          <p className="muted small" role="status">{t('rosterList.shown', { count: rows.length, total: roster.data.length })}</p>
          <div className="table-wrap">
            <table className="table">
              <caption className="visually-hidden">{t('rosterList.title')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('roster.studentId')}</th>
                  <th scope="col">{t('roster.name')}</th>
                  <th scope="col">{t('rosterList.email')}</th>
                  <th scope="col">{t('rosterList.account')}</th>
                  <th scope="col">{t('rosterList.source')}</th>
                  <th scope="col">{t('roster.status')}</th>
                  <th scope="col">{t('groups.code')}</th>
                  <th scope="col">{t('adminCohorts.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 500).map((r) => (
                  <tr key={r.personId} data-testid="roster-row">
                    <td>{r.studentId}</td>
                    <td>{name(r)}</td>
                    <td>{r.email ?? '—'}</td>
                    <td>{r.accountLinked ? t('rosterList.linked') : t('rosterList.notLinked')}</td>
                    <td>{t(`rosterList.sources.${r.source}`)} <span className="muted small">{f.date(r.enrolledAt)}</span></td>
                    <td>{t(`status.${r.status ?? 'active'}`)}</td>
                    <td>{r.projectCode ?? '—'}</td>
                    <td>
                      <Button variant="secondary" aria-label={t('status.recordLabel', { name: name(r) })} onClick={() => {
                        setTarget(r);
                        setForm({ status: '', date: '', docRef: '', reason: '' });
                        setError(null);
                        record.reset();
                      }}>{t('status.record')}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 500 ? <p className="muted small">{t('rosterList.truncated')}</p> : null}
        </>
      ) : null}
      <Dialog open={target !== null} title={t('status.title', { name: target ? name(target) : '' })} onCancel={() => setTarget(null)} actions={(
        <>
          <Button busy={record.isPending} onClick={confirm}>{t('adminCohorts.confirm')}</Button>
          <Button variant="secondary" onClick={() => setTarget(null)}>{t('common.cancel')}</Button>
        </>
      )}>
        <p className="muted small">{t('status.note')}</p>
        <SelectField label={t('roster.status')} value={form.status} required
          options={STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) }))} onChange={(v) => setForm((x) => ({ ...x, status: v }))} />
        <TextField label={t('status.effective')} type="date" value={form.date} required onChange={(e) => {
          const v = e.currentTarget.value;
          setForm((x) => ({ ...x, date: v }));
        }} />
        <TextField label={t('status.docRef')} value={form.docRef} maxLength={200} onChange={(e) => {
          const v = e.currentTarget.value;
          setForm((x) => ({ ...x, docRef: v }));
        }} />
        <TextArea label={t('group.reason')} value={form.reason} required maxLength={2000} onChange={(e) => {
          const v = e.currentTarget.value;
          setForm((x) => ({ ...x, reason: v }));
        }} />
        {error ? <Alert tone="error"><p>{error}</p></Alert> : null}
        <ErrorAlert error={record.error} />
      </Dialog>
    </section>
  );
}
